import type { Options } from "qr-code-styling"

// Forest and timber color values from theme
const FOREST_COLOR = "#628562" // forest[5]
const TIMBER_COLOR = "#9e8a66" // timber[5]

/**
 * The one branded QR look — forest/timber gradient, rounded dots, optionally an embedded logo
 * — shared by the client-side `QRCode` component and any server-side generator that needs the
 * exact same styling (e.g. an API route producing a QR for a merchant's own fulfillment
 * pipeline). Kept in its own plain module (no `"use client"`) specifically so it's importable
 * from server code, which a client-directived file can't be.
 *
 * `logoSrc: null` omits the embedded logo entirely — needed server-side under plain `jsdom`
 * (no `nodeCanvas`): qr-code-styling's image-loading path never resolves there (verified
 * directly — `getRawData` hangs indefinitely with an `image` set and no `nodeCanvas`), while
 * the gradient/rounded/corner styling all render correctly on their own. Every browser caller
 * should keep passing a real `logoSrc`; only a Node-without-`nodeCanvas` caller needs `null`.
 */
export function buildQrOptions(logoSrc: string | null): Partial<Options> {
  const options: Partial<Options> = {
    dotsOptions: {
      gradient: {
        type: "linear",
        rotation: 1.5,
        colorStops: [
          { offset: 0, color: FOREST_COLOR },
          { offset: 1, color: TIMBER_COLOR },
        ],
      },
      type: "rounded",
    },
    qrOptions: {
      errorCorrectionLevel: "Q",
    },
    cornersSquareOptions: {
      type: "extra-rounded",
      color: FOREST_COLOR,
    },
    cornersDotOptions: {
      type: "dot",
      color: TIMBER_COLOR,
    },
    backgroundOptions: {
      color: "#fff",
      round: 0.1,
    },
  }

  if (logoSrc) {
    options.image = logoSrc
    options.imageOptions = {
      crossOrigin: "anonymous",
      hideBackgroundDots: true,
      imageSize: 0.5,
    }
  }

  return options
}
